import { create } from "zustand";
import { persist } from "zustand/middleware";
import { DEFAULT_GROUP_NAMES } from "@/data/DEFAULT_GROUP_NAMES";
import { mergeInsuranceParamsWithCatalogTemplate } from "@/lib/mergeInsuranceParamsWithCatalogTemplate";
import type {
	CreateAccount,
	CreateAccountFormData,
	UpdateAccount,
} from "@/types/account/account";
import type {
	AccountTree,
	CreateAccountTree,
	UpdateAccountTree,
} from "@/types/accountTree/accountTree";
import type {
	CreateGroup,
	CreateGroupFormData,
	UpdateGroup,
} from "@/types/group/group";
import type {
	CreateInsurance,
	CreateInsuranceFormData,
	UpdateInsurance,
} from "@/types/insurance/insurance";
import { insuranceDisplayRank } from "@/types/insurance/insurance";
import type {
	CreateMainAccount,
	CreateMainAccountFormData,
	UpdateMainAccount,
} from "@/types/mainAccount/mainAccount";
import type { CreateParameterFormData } from "@/types/parameter/parameter";

export interface CreateAccountState {
	isShowingResetModal: boolean;

	mainAccount: CreateMainAccountFormData;
	accounts: CreateAccountFormData[];
	groups: CreateGroupFormData[];
	insurances: CreateInsuranceFormData[];
	/**
	 * Canonical insurance templates loaded from the backend product-definition
	 * endpoint. Used to seed `insurances` and to look up default values when
	 * detecting `isAltered` or resetting parameters.
	 */
	productDefinitions: CreateInsuranceFormData[];

	setFullAccountTree: (tree: AccountTree) => void;

	/**
	 * Set the catalog of available products. If `insurances` is empty (no
	 * persisted draft), it is seeded with a deep clone of the templates so
	 * the form is immediately interactive.
	 */
	setProductDefinitions: (defs: CreateInsuranceFormData[]) => void;

	setMainAccount: (mainAccount: CreateMainAccountFormData) => void;

	addAccount: () => void;
	editAccount: (newAccount: CreateAccountFormData) => void;
	removeAccount: (accountID: string) => void;

	addGroup: (accountID: string) => void;
	editGroup: (newGroup: CreateGroupFormData) => void;
	removeGroup: (groupID: string) => void;

	addInsuranceBelow: (index: number) => void;
	removeInsurance: (id: string) => void;

	setProviderForAllInsurances: (provider: string) => void;
	setProviderForInsurance: (insuranceID: string, provider: string) => void;

	isSelectedForAllInsurances: () => boolean;
	isSelectedForInsurance: (insuranceID: string) => boolean;

	clearGroupsFromAllInsurances: () => void;
	assignGroupsToAllInsurances: () => void;
	toggleGroupsForAllInsurances: () => void;

	clearGroupsFromInsurance: (insuranceID: string) => void;
	assignGroupsToInsurance: (insuranceID: string) => void;
	toggleGroupsForInsurance: (insuranceID: string) => void;
	toggleSingleGroupForInsurance: (
		insurance: CreateInsuranceFormData,
		groupID: string,
	) => void;

	setIsShoingResetModal: (isShowing: boolean) => void;
	editParameter: (
		insurance: CreateInsuranceFormData,
		parameter: CreateParameterFormData,
	) => void;
	resetParameter: (
		insurance: CreateInsuranceFormData,
		parameter: CreateParameterFormData,
	) => void;

	/**
	 * Wipe a row's parameters and re-pull a fresh set from the matching
	 * product-definition template. Provider, groups, mode, and id are kept;
	 * only `parameters` is replaced. Used by the hidden "refresh-from-catalog"
	 * debug action to bring outdated rows back in sync with the canonical
	 * default parameter set when the catalog evolves.
	 */
	refreshProductParameters: (insuranceID: string) => void;

	checkStorage: () => void;

	formatForCreate: () => CreateAccountTree;
	formatForUpdate: () => UpdateAccountTree;

	reset: () => void;
}

import { toast } from "sonner";
// accountStoreLogic.ts
import type { StateCreator } from "zustand";
import { sleep } from "@/lib/utils";

const createAccountStoreLogic: StateCreator<CreateAccountState> = (
	set,
	get,
) => ({
	isShowingResetModal: false,

	mainAccount: {
		id: crypto.randomUUID(),
		type: "create",
		display_name: "",
		orgnr: "",
		error: null,
	},
	accounts: [],
	groups: [],
	insurances: [],
	productDefinitions: [],

	setProductDefinitions: (defs) => {
		set((state) => ({
			productDefinitions: defs,
			// Lazy seed: only populate the form's insurances list when no draft
			// is present. This preserves any in-progress work the user did on a
			// previous session that the persist middleware restored.
			insurances:
				state.insurances.length === 0
					? (structuredClone(defs) as CreateInsuranceFormData[]).sort(
							(a, b) =>
								insuranceDisplayRank(a.type) - insuranceDisplayRank(b.type),
						)
					: state.insurances,
		}));
	},

	setFullAccountTree: (tree: AccountTree) => {
		const productDefs = get().productDefinitions;
		const seenInsuranceTypes = new Set<string>();

		const existingRows: CreateInsuranceFormData[] = tree.insurances.map(
			(insurance) => {
				const isRemoveable = seenInsuranceTypes.has(insurance.type);
				seenInsuranceTypes.add(insurance.type);

				return {
					id: insurance.id,
					provider: insurance.provider,
					mode: "update" as "create" | "update",
					type: insurance.type,
					groups: insurance.groups,
					error: null,
					parameters: insurance.params.map((param) => ({
						...param,
						error: null,
					})),
					isRemovable: isRemoveable,
					modified_at: insurance.modified_at ?? null,
					modified_by: insurance.modified_by ?? null,
				};
			},
		);

		// For every product definition the customer doesn't yet have, append a
		// fresh `mode: "create"` template row so admins can enable newly-added
		// products on existing customers. The clones already carry default
		// parameter values, empty groups, and `isRemovable: false`.
		const missingTemplateRows = productDefs
			.filter((def) => !seenInsuranceTypes.has(def.type))
			.map((def) => structuredClone(def) as CreateInsuranceFormData);

		set({
			mainAccount: {
				id: tree.main_account.main_account_id,
				type: "update",
				display_name: tree.main_account.display_name,
				orgnr: tree.main_account.org_number,
				error: null,
			},
			accounts: tree.accounts.map((account) => ({
				id: account.id,
				type: "update",
				name: account.display_name,
				orgnr: account.org_number,
				contactPerson: account.contact_name,
				email: account.email,
				phone: account.phone_nr,
				error: null,
			})),
			groups: tree.accounts.flatMap((account) =>
				account.groups.map((group) => ({
					id: group.id,
					type: "update",
					accountID: group.account_id,
					name: group.name,
					error: null,
				})),
			),
			insurances: [...existingRows, ...missingTemplateRows].sort(
				(a, b) => insuranceDisplayRank(a.type) - insuranceDisplayRank(b.type),
			),
		});
	},
	setMainAccount: (mainAccount) => {
		set({ mainAccount });
	},

	addAccount: () => {
		const newAccount: CreateAccountFormData = {
			id: crypto.randomUUID(),
			type: "create",
			name: "",
			orgnr: "",
			phone: "",
			email: "",
			contactPerson: "",
			error: null,
		};

		const newGroups: CreateGroupFormData[] = DEFAULT_GROUP_NAMES.map(
			(name) => ({
				id: crypto.randomUUID(),
				type: "create",
				accountID: newAccount.id,
				name: name,
				error: null,
			}),
		);

		set((state) => ({
			accounts: [...state.accounts, newAccount],
			groups: [...state.groups, ...newGroups],
			insurances: state.insurances.map((insurance) => {
				if (insurance.type === "wc") {
					return {
						...insurance,
						groups: insurance.groups.concat(newGroups.map((group) => group.id)),
					};
				}
				return insurance; // Return the unchanged insurance for non-wc types
			}),
		}));
	},
	editAccount: (newAccount) => {
		set((state) => ({
			accounts: state.accounts.map((account) =>
				account.id === newAccount.id ? newAccount : account,
			),
		}));
	},
	removeAccount: (accountID) => {
		const groupsToRemove: string[] = get()
			.groups.filter((group) => group.accountID === accountID)
			.map((group) => group.id);

		set((state) => ({
			accounts: state.accounts.filter((account) => account.id !== accountID),
			groups: state.groups.filter(
				(group) => !groupsToRemove.includes(group.id),
			),
			insurances: state.insurances.map((insurance) => ({
				...insurance,
				groups: insurance.groups.filter(
					(groupID) => !groupsToRemove.includes(groupID),
				),
			})),
		}));
	},

	addGroup: (accountID) => {
		const newGroup: CreateGroupFormData = {
			id: crypto.randomUUID(),
			type: "create",
			accountID: accountID,
			name: "",
			error: null,
		};
		set((state) => ({
			groups: [...state.groups, newGroup],
			insurances: state.insurances.map((insurance) => {
				if (insurance.type === "wc") {
					return {
						...insurance,
						groups: insurance.groups.concat(newGroup.id),
					};
				}
				return insurance; // Return the unchanged insurance for non-wc types
			}),
		}));
	},
	editGroup: (newGroup) => {
		set((state) => ({
			groups: state.groups.map((group) =>
				group.id === newGroup.id ? newGroup : group,
			),
		}));
	},
	removeGroup: (groupID) => {
		set((state) => ({
			groups: state.groups.filter((group) => group.id !== groupID),
			insurances: state.insurances.map((insurance) => ({
				...insurance,
				groups: insurance.groups.filter((gID) => gID !== groupID),
			})),
		}));
	},

	addInsuranceBelow(index) {
		const currentInsurance = get().insurances[index];
		const currentParameters = currentInsurance.parameters;

		// Make a safe copy of the object:
		const newInsurance: CreateInsuranceFormData = {
			...currentInsurance,
			id: crypto.randomUUID(), // ensure new unique id
			isRemovable: true,
			mode: "create",
			groups: [],
			error: null,
			modified_at: undefined,
			modified_by: undefined,
			parameters: currentParameters?.map((param) => ({
				...param,
				id: crypto.randomUUID(), // ensure new unique id
			})),
		};

		set((state) => ({
			insurances: [
				...state.insurances.slice(0, index + 1),
				newInsurance,
				...state.insurances.slice(index + 1),
			],
		}));
	},
	removeInsurance(insuranceID) {
		const object = get();
		const insurance = object.insurances.find(
			(insurance) => insurance.id === insuranceID,
		);
		if (!insurance) return false;
		if (!insurance.isRemovable) return false;
		set((state) => ({
			insurances: state.insurances.filter(
				(insurance) => insurance.id !== insuranceID,
			),
		}));
	},

	setProviderForAllInsurances: (provider: string) => {
		set((state) => ({
			insurances: state.insurances.map((insurance) => ({
				...insurance,
				provider: provider,
			})),
		}));
	},

	setProviderForInsurance: (insuranceID: string, provider: string) => {
		set((state) => ({
			insurances: state.insurances.map((insurance) =>
				insurance.id === insuranceID
					? { ...insurance, provider: provider }
					: insurance,
			),
		}));
	},

	isSelectedForAllInsurances: () => {
		const object = get();
		return object.insurances.some(
			(insurance) => insurance.groups.length > 0 || object.groups.length === 0,
		);
	},
	isSelectedForInsurance: (insuranceID) => {
		const object = get();
		const insurance = object.insurances.find(
			(insurance) => insurance.id === insuranceID,
		);
		if (!insurance) return false;

		if (insurance.type === "wc") {
			return insurance.groups.length > 0 || object.groups.length === 0;
		}

		return insurance.groups.length > 0;
	},

	clearGroupsFromAllInsurances: () => {
		set((state) => ({
			insurances: state.insurances.map((insurance) => ({
				...insurance,
				groups: [],
			})),
		}));
	},
	assignGroupsToAllInsurances: () => {
		set((state) => ({
			insurances: state.insurances.map((insurance) => ({
				...insurance,
				groups: [...state.groups.map((group) => group.id)],
			})),
		}));
	},
	toggleGroupsForAllInsurances: () => {
		const object = get();
		if (object.isSelectedForAllInsurances()) {
			object.clearGroupsFromAllInsurances();
		} else {
			object.assignGroupsToAllInsurances();
		}
	},

	clearGroupsFromInsurance: (insuranceID) => {
		set((state) => ({
			insurances: state.insurances.map((insurance) =>
				insurance.id === insuranceID
					? { ...insurance, groups: [] } // Clear groups
					: insurance,
			),
		}));
	},
	assignGroupsToInsurance: async (insuranceID) => {
		const object = get();

		const insurance = object.insurances.find(
			(insurance) => insurance.id === insuranceID,
		);

		if (!insurance) return;

		const otherInsurancesOfSameType = object.insurances.filter(
			(otherInsurance) =>
				otherInsurance.type === insurance.type &&
				otherInsurance.id !== insurance.id,
		);

		const otherInsuranceGroups = otherInsurancesOfSameType.flatMap(
			(otherInsurance) => otherInsurance.groups,
		);

		const groupsToAdd = object.groups.filter(
			(group) => !otherInsuranceGroups.includes(group.id),
		);

		if (groupsToAdd.length === 0) {
			toast.error("Alle grupper har allerede denne forsikringen");
			set((state) => ({
				insurances: state.insurances.map((prevInsurance) => {
					if (
						prevInsurance.type === insurance.type &&
						prevInsurance.id !== insuranceID
					) {
						const error = "Gruppen har allerede denne forsikringen";
						return { ...prevInsurance, error: error };
					}

					return prevInsurance;
				}),
			}));
			await sleep(3500);
			set((state) => ({
				insurances: state.insurances.map((prevInsurance) => {
					if (
						prevInsurance.error === "Gruppen har allerede denne forsikringen" &&
						prevInsurance.type === insurance.type
					) {
						const error = null;
						return { ...prevInsurance, error: error };
					}

					return prevInsurance;
				}),
			}));
			return;
		}

		set((state) => {
			return {
				insurances: state.insurances.map((insurance) =>
					insurance.id === insuranceID
						? {
								...insurance,
								groups: [...groupsToAdd.map((group) => group.id)],
							}
						: insurance,
				),
			};
		});
	},
	toggleGroupsForInsurance: (insuranceID) => {
		const object = get();
		if (object.isSelectedForInsurance(insuranceID)) {
			object.clearGroupsFromInsurance(insuranceID);
		} else {
			object.assignGroupsToInsurance(insuranceID);
		}
	},
	toggleSingleGroupForInsurance: async (insurance, groupID) => {
		const object = get();

		const otherInsurancesOfSameType = object.insurances.filter(
			(otherInsurance) =>
				otherInsurance.type === insurance.type &&
				otherInsurance.id !== insurance.id,
		);

		const otherInsuranceGroups = otherInsurancesOfSameType.flatMap(
			(otherInsurance) => otherInsurance.groups,
		);

		const isGroupInOtherInsurances = otherInsuranceGroups.includes(groupID);

		if (isGroupInOtherInsurances) {
			toast.error("Gruppen har allerede denne forsikringen");
			set((state) => ({
				insurances: state.insurances.map((prevInsurance) => {
					if (
						prevInsurance.groups.includes(groupID) &&
						prevInsurance.type === insurance.type
					) {
						const error = "Gruppen har allerede denne forsikringen";
						return { ...prevInsurance, error: error };
					}

					return prevInsurance;
				}),
			}));

			await sleep(3500);

			set((state) => ({
				insurances: state.insurances.map((prevInsurance) => {
					if (
						prevInsurance.error === "Gruppen har allerede denne forsikringen" &&
						prevInsurance.type === insurance.type
					) {
						const error = null;
						return { ...prevInsurance, error: error };
					}

					return prevInsurance;
				}),
			}));
			return;
		}

		set((state) => ({
			insurances: state.insurances.map((prevInsurance) => {
				if (prevInsurance.id === insurance.id) {
					// Ensure groups is always treated as an array
					const prevGroups = Array.isArray(prevInsurance.groups)
						? prevInsurance.groups
						: [];

					const isIncluded = prevGroups.includes(groupID);

					const newGroups = isIncluded
						? prevGroups.filter((id) => id !== groupID) // Remove if already included
						: [...prevGroups, groupID]; // Add if not included

					return { ...prevInsurance, groups: newGroups };
				}

				return prevInsurance;
			}),
		}));
	},

	editParameter(insurance, parameter) {
		const defaultInsurance = get().productDefinitions.find(
			(val) => val.type === insurance.type,
		);

		if (!defaultInsurance) {
			console.error("Insurance not found in product definitions");
			return;
		}

		const defaultValue = defaultInsurance.parameters?.find((defaultParam) => {
			return defaultParam.type === parameter.type;
		})?.value;

		parameter.isAltered = parameter.value !== defaultValue;

		set((state) => ({
			insurances: state.insurances.map((insurance) => {
				if (insurance.parameters) {
					return {
						...insurance,
						parameters: insurance.parameters.map((param) => {
							return param.id === parameter.id ? param : param;
						}),
					};
				}
				return insurance;
			}),
		}));
	},
	resetParameter(insurance, parameter) {
		const defaultInsurance = get().productDefinitions.find(
			(val) => val.type === insurance.type,
		);

		if (!defaultInsurance) {
			console.error("Insurance not found in product definitions");
			return;
		}

		const defaultValue = defaultInsurance.parameters?.find((defaultParam) => {
			return defaultParam.type === parameter.type;
		})?.value as typeof parameter.value;

		parameter.isAltered = false;
		parameter.value = defaultValue ?? "";
		parameter.error = null;

		set((state) => ({
			insurances: state.insurances.map((prevInsurance) => {
				if (prevInsurance.id === insurance.id && prevInsurance.parameters) {
					return {
						...prevInsurance,
						parameters: prevInsurance.parameters.map((param) => {
							return param.id === parameter.id ? parameter : param;
						}),
					};
				}
				return prevInsurance;
			}),
		}));
	},

	refreshProductParameters: (insuranceID) => {
		const state = get();
		const target = state.insurances.find((ins) => ins.id === insuranceID);
		if (!target) {
			console.warn(
				`[refreshProductParameters] insurance ${insuranceID} not found`,
			);
			return;
		}

		const template = state.productDefinitions.find(
			(def) => def.type === target.type,
		);
		if (!template) {
			console.warn(
				`[refreshProductParameters] no template for type "${target.type}"`,
			);
			return;
		}

		const templateParams = template.parameters ?? [];
		const templateTypeSet = new Set(templateParams.map((p) => p.type));

		const mergedParameters = mergeInsuranceParamsWithCatalogTemplate(
			{ params: target.parameters ?? [] },
			template,
		);

		// Warn in dev if any existing types were dropped (extra params).
		if (import.meta.env.DEV) {
			for (const p of target.parameters ?? []) {
				if (!templateTypeSet.has(p.type)) {
					console.info(
						`[refreshProductParameters] dropping stale param type "${p.type}" from insurance ${insuranceID}`,
					);
				}
			}
		}

		set((current) => ({
			insurances: current.insurances.map((ins) =>
				ins.id === insuranceID
					? { ...ins, parameters: mergedParameters, error: null }
					: ins,
			),
		}));
	},

	formatForCreate: () => {
		const object = get();

		const mainAccount: CreateMainAccount = {
			display_name: object.mainAccount.display_name,
			org_number: object.mainAccount.orgnr,
		};

		const mapToCreateGroup = (group: CreateGroupFormData): CreateGroup => ({
			tempid: group.id,
			name: group.name,
		});

		const mapToCreateAccount = (
			account: CreateAccountFormData,
		): CreateAccount => ({
			display_name: account.name,
			org_number: account.orgnr,
			contact_name: account.contactPerson,
			phone_nr: account.phone,
			email: account.email,
			groups: object.groups
				.filter((group) => group.accountID === account.id)
				.map(mapToCreateGroup),
		});

		const mapToCreateInsurance = (
			insurance: CreateInsuranceFormData,
		): CreateInsurance => ({
			type: insurance.type,
			provider: insurance.provider,
			params: insurance.parameters ?? [],
			groups: insurance.groups,
			created_at: new Date().toISOString(),
		});

		const formattedData: CreateAccountTree = {
			main_account: mainAccount,
			accounts: object.accounts.map(mapToCreateAccount),
			insurances: object.insurances.map(mapToCreateInsurance),
		};

		return formattedData;
	},
	formatForUpdate: () => {
		const object = get();

		const mainAccount: UpdateMainAccount = {
			main_account_id: object.mainAccount.id,
			display_name: object.mainAccount.display_name,
			org_number: object.mainAccount.orgnr,
		};

		const mapToCreateGroup = (
			group: CreateGroupFormData,
		): CreateGroup | UpdateGroup =>
			group.type === "create"
				? ({
						tempid: group.id,
						name: group.name,
						account_id: crypto.randomUUID(),
					} as CreateGroup)
				: ({
						id: group.id,
						name: group.name,
						account_id: crypto.randomUUID(),
					} as UpdateGroup);

		const mapToCreateAccount = (
			account: CreateAccountFormData,
		): CreateAccount | UpdateAccount =>
			account.type === "create"
				? ({
						display_name: account.name,
						org_number: account.orgnr,
						contact_name: account.contactPerson,
						phone_nr: account.phone,
						email: account.email,
						groups: object.groups
							.filter((group) => group.accountID === account.id)
							.map(mapToCreateGroup),
					} as CreateAccount)
				: ({
						id: account.id,
						display_name: account.name,
						org_number: account.orgnr,
						contact_name: account.contactPerson,
						phone_nr: account.phone,
						email: account.email,
						groups: object.groups
							.filter((group) => group.accountID === account.id)
							.map(mapToCreateGroup),
					} as UpdateAccount);

		const mapToCreateInsurance = (
			insurance: CreateInsuranceFormData,
		): UpdateInsurance | CreateInsurance =>
			insurance.mode === "update"
				? ({
						id: insurance.id,
						type: insurance.type,
						provider: insurance.provider,
						params: insurance.parameters ?? [],
						groups: insurance.groups,
					} as UpdateInsurance)
				: ({
						type: insurance.type,
						provider: insurance.provider,
						params: insurance.parameters ?? [],
						groups: insurance.groups,
						created_at: new Date().toISOString(),
					} as CreateInsurance);

		const formattedData: UpdateAccountTree = {
			main_account: mainAccount,
			accounts: object.accounts.map(mapToCreateAccount),
			insurances: object.insurances.map(mapToCreateInsurance),
		};

		return formattedData;
	},

	checkStorage: () => {
		const { mainAccount, accounts, groups } = get();

		if (
			!!mainAccount.display_name ||
			accounts.length > 0 ||
			groups.length > 0
		) {
			set({ isShowingResetModal: true });
		}
	},

	setIsShoingResetModal: (isShowing: boolean) => {
		set({ isShowingResetModal: isShowing });
	},

	reset: () =>
		set((state) => ({
			mainAccount: {
				id: crypto.randomUUID(),
				type: "create",
				display_name: "",
				orgnr: "",
				error: null,
			},
			accounts: [],
			groups: [],
			// Rebuild from the canonical templates fetched from the backend. If
			// definitions haven't loaded yet (edge case after a hard reset), the
			// page-level effect will reseed once `setProductDefinitions` runs.
			insurances: structuredClone(
				state.productDefinitions,
			) as CreateInsuranceFormData[],
			isShowingResetModal: false,
		})),
});

export const useCreateAccountStore = create<CreateAccountState>()(
	persist(createAccountStoreLogic, {
		name: "create-account-store",
		onRehydrateStorage: (persistedState) => {
			setTimeout(() => {
				persistedState.checkStorage();
			}, 500);
		},
	}),
);

export const useUpdateAccountStore = create<CreateAccountState>()(
	createAccountStoreLogic,
);
